/**
 * Design Phase API Service
 *
 * Handles all API calls for the Design Phase workflow:
 * - Step 6: Data Flow Mapping (Sankey visualization)
 * - Step 7: Integration Pattern Selection
 * - Step 8: Code Generation
 */

import axios from 'axios';
import type {
  DataFlowVisualization,
  DataFlowResponse,
  ColumnMapping,
  ColumnMappingUpdate,
  RelationshipMapping,
  UnmappedColumn,
  CoverageSummary,
  IntegrationDesignPlan,
  IntegrationPlanSummary,
  EntityIntegrationPlan,
  PatternRecommendation,
  PatternComparisonResponse,
  InfrastructureRequirements,
  ExecutionPhase,
  GenerateIntegrationPlanRequest,
  UpdateEntityPatternRequest,
  ConfirmIntegrationPlanRequest,
  IntegrationPackage,
  GenerateCodeRequest,
  GenerateCodeResponse,
  PackageSummary,
  CodeArtifact,
} from '../../types/designPhase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://graph-solution-backend.huhoka.com/api';

// Create dedicated axios instance for design phase
const designClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 1 minute default
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
designClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Increase timeout for AI-powered endpoints
    if (config.url?.includes('/generate') || config.url?.includes('/recommendations')) {
      config.timeout = 180000; // 3 minutes for AI operations
    }

    // Code generation can take longer
    if (config.url?.includes('code-generation')) {
      config.timeout = 300000; // 5 minutes for code generation
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================================================
// Step 6: Data Flow Mapping API
// =============================================================================

/**
 * Generate data flow visualization (Sankey diagram data)
 */
export const generateDataFlow = async (
  caseId: string,
  options: {
    include_ai_inference?: boolean;
    confidence_threshold?: number;
    force_regenerate?: boolean;
  } = {}
): Promise<DataFlowResponse> => {
  const response = await designClient.post(
    `/data-flow/cases/${caseId}/generate`,
    options
  );
  return response.data;
};

/**
 * Get current data flow visualization for a case
 * Note: Backend returns DataFlowSummaryResponse, use getDataFlowVisualization for full data
 */
export const getDataFlow = async (caseId: string): Promise<DataFlowVisualization> => {
  const response = await designClient.get(`/data-flow/cases/${caseId}/visualization`);
  return response.data;
};

/**
 * Get all column mappings for a case
 */
export const getColumnMappings = async (caseId: string): Promise<ColumnMapping[]> => {
  const response = await designClient.get(`/data-flow/cases/${caseId}/mappings`);
  return response.data;
};

/**
 * Update a column mapping
 */
export const updateColumnMapping = async (
  caseId: string,
  mappingId: string,
  update: ColumnMappingUpdate
): Promise<ColumnMapping> => {
  const response = await designClient.patch(
    `/data-flow/cases/${caseId}/mappings/${mappingId}`,
    update
  );
  return response.data;
};

/**
 * Create a manual column mapping
 */
export const createColumnMapping = async (
  caseId: string,
  mapping: Partial<ColumnMapping>
): Promise<ColumnMapping> => {
  const response = await designClient.post(
    `/data-flow/cases/${caseId}/mappings`,
    mapping
  );
  return response.data;
};

/**
 * Persist the current data flow to Neo4j.
 *
 * Backend route: POST /data-flow/cases/{caseId}/save
 *
 * Pass the mappings currently on screen so the saved artifact reflects the
 * user's edits — omitting them makes the backend persist a fresh regeneration
 * instead. Pass `visualization_id` from the loaded flow so repeated saves
 * update the same DataFlowMapping node rather than creating new ones.
 *
 * This is what Step 7 and Step 8 later read: without a save, the Integration
 * Plan and the generated package are built from a regeneration the user never
 * reviewed.
 */
export const saveDataFlow = async (
  caseId: string,
  payload: {
    column_mappings?: ColumnMapping[];
    relationship_mappings?: RelationshipMapping[];
    visualization_id?: string;
    save_mappings?: boolean;
    save_coverage?: boolean;
  } = {}
): Promise<{
  success: boolean;
  message: string;
  visualization_id: string;
  column_mappings_saved: number;
  relationship_mappings_saved: number;
  coverage_score: number;
}> => {
  const response = await designClient.post(
    `/data-flow/cases/${caseId}/save`,
    { save_mappings: true, save_coverage: true, ...payload }
  );
  return response.data;
};

/**
 * Run AI inference over the columns that automatic mapping could not resolve.
 *
 * Backend route: POST /data-flow/cases/{caseId}/ai-inference
 */
export const runAiInference = async (
  caseId: string,
  options: { confidence_threshold?: number } = {}
): Promise<DataFlowVisualization> => {
  const response = await designClient.post(
    `/data-flow/cases/${caseId}/ai-inference`,
    options
  );
  return response.data;
};

/**
 * Get relationship mappings for a case
 */
export const getRelationshipMappings = async (caseId: string): Promise<RelationshipMapping[]> => {
  const response = await designClient.get(`/data-flow/cases/${caseId}/relationship-mappings`);
  return response.data;
};

/**
 * Get unmapped columns for a case
 */
export const getUnmappedColumns = async (caseId: string): Promise<UnmappedColumn[]> => {
  const response = await designClient.get(`/data-flow/cases/${caseId}/unmapped`);
  return response.data;
};

/**
 * Get mapping suggestions for an unmapped column
 */
export const getMappingSuggestions = async (
  caseId: string,
  columnId: string
): Promise<Array<{ target_node_label: string; target_property: string; confidence: number }>> => {
  const response = await designClient.get(
    `/data-flow/cases/${caseId}/unmapped/${columnId}/suggestions`
  );
  return response.data;
};

/**
 * Get coverage summary for a case
 */
export const getCoverageSummary = async (caseId: string): Promise<CoverageSummary> => {
  const response = await designClient.get(`/data-flow/cases/${caseId}/coverage`);
  return response.data;
};

/**
 * Get data flow statistics for a case
 */
export const getDataFlowStats = async (
  caseId: string
): Promise<{
  total_source_columns: number;
  mapped_columns: number;
  mapping_percent: number;
  total_relationships: number;
  mapped_relationships: number;
  high_confidence_count: number;
  medium_confidence_count: number;
  low_confidence_count: number;
}> => {
  const response = await designClient.get(`/data-flow/cases/${caseId}/stats`);
  return response.data;
};

// =============================================================================
// Step 7: Integration Pattern API
// =============================================================================

/**
 * Generate integration plan with AI recommendations
 */
export const generateIntegrationPlan = async (
  caseId: string,
  options: GenerateIntegrationPlanRequest = {}
): Promise<IntegrationDesignPlan> => {
  const response = await designClient.post(
    `/integration-pattern/cases/${caseId}/generate`,
    options
  );
  return response.data;
};

// The /integration-pattern endpoints wrap their payload in a
// `{ success, <key>: ... }` envelope. These helpers unwrap it so callers get
// the shape the return type promises.
//
// This mismatch was invisible while no IntegrationPlan was ever persisted:
// the routes 404'd, TanStack left `data` undefined, and the components' `|| []`
// guards absorbed it. Once plans persist, the routes return 200 and an
// un-unwrapped envelope reaches `.map()` as an object — which blanks the page.

/**
 * Get integration plan summary for a case
 */
export const getIntegrationPlanSummary = async (caseId: string): Promise<IntegrationPlanSummary> => {
  const response = await designClient.get(`/integration-pattern/cases/${caseId}`);
  return response.data?.summary ?? response.data;
};

/**
 * Get full integration plan for a case
 */
export const getIntegrationPlan = async (caseId: string): Promise<IntegrationDesignPlan> => {
  const response = await designClient.get(`/integration-pattern/cases/${caseId}/plan`);
  return response.data?.plan ?? response.data;
};

/**
 * Get entity integration plans for a case
 */
export const getEntityPlans = async (caseId: string): Promise<EntityIntegrationPlan[]> => {
  const response = await designClient.get(`/integration-pattern/cases/${caseId}/entities`);
  return response.data?.entities ?? [];
};

/**
 * Get a single entity's integration plan
 */
export const getEntityPlan = async (
  caseId: string,
  entityLabel: string
): Promise<EntityIntegrationPlan> => {
  const response = await designClient.get(
    `/integration-pattern/cases/${caseId}/entities/${encodeURIComponent(entityLabel)}`
  );
  return response.data;
};

/**
 * Update an entity's integration pattern
 */
export const updateEntityPattern = async (
  caseId: string,
  entityLabel: string,
  update: UpdateEntityPatternRequest
): Promise<EntityIntegrationPlan> => {
  const response = await designClient.patch(
    `/integration-pattern/cases/${caseId}/entities/${encodeURIComponent(entityLabel)}`,
    update
  );
  return response.data;
};

/**
 * Get AI recommendations for integration patterns
 */
export const getPatternRecommendations = async (
  caseId: string
): Promise<PatternRecommendation[]> => {
  const response = await designClient.get(`/integration-pattern/cases/${caseId}/recommendations`);
  return response.data?.recommendations ?? [];
};

/**
 * Get pattern comparison for an entity
 */
export const getPatternComparison = async (
  caseId: string,
  entityLabel: string
): Promise<PatternComparisonResponse> => {
  const response = await designClient.get(
    `/integration-pattern/cases/${caseId}/entities/${encodeURIComponent(entityLabel)}/comparison`
  );
  return response.data;
};

/**
 * Get infrastructure requirements for the integration plan
 */
export const getInfrastructureRequirements = async (
  caseId: string
): Promise<InfrastructureRequirements> => {
  const response = await designClient.get(`/integration-pattern/cases/${caseId}/infrastructure`);
  // The route nests the requirements one level deeper than the envelope key
  // suggests: { success, infrastructure: { case_id, infrastructure, costs… } }.
  const block = response.data?.infrastructure ?? response.data;
  return block?.infrastructure ?? block;
};

/**
 * Get execution phases for the integration plan
 */
export const getExecutionPhases = async (caseId: string): Promise<ExecutionPhase[]> => {
  const response = await designClient.get(`/integration-pattern/cases/${caseId}/execution-phases`);
  return response.data?.phases ?? [];
};

/**
 * Confirm integration plan and proceed to code generation
 */
export const confirmIntegrationPlan = async (
  caseId: string,
  request: ConfirmIntegrationPlanRequest = { confirmed: true }
): Promise<{ success: boolean; message: string; plan_id: string }> => {
  const response = await designClient.post(
    `/integration-pattern/cases/${caseId}/confirm`,
    request
  );
  return response.data;
};

/**
 * Get question coverage for the integration plan
 */
export const getQuestionCoverage = async (
  caseId: string
): Promise<{
  total_questions: number;
  covered_questions: number;
  coverage_percent: number;
  questions: Array<{
    question_id: string;
    question_text: string;
    is_covered: boolean;
    covering_entities: string[];
    covering_patterns: string[];
  }>;
}> => {
  const response = await designClient.get(
    `/integration-pattern/cases/${caseId}/question-coverage`
  );
  return response.data;
};

// =============================================================================
// Step 8: Code Generation API
// =============================================================================

/**
 * Generate integration code package
 */
export const generateIntegrationPackage = async (
  caseId: string,
  options: GenerateCodeRequest = {}
): Promise<GenerateCodeResponse> => {
  const response = await designClient.post(
    `/code-generation/cases/${caseId}/generate`,
    options
  );
  return response.data;
};

/**
 * Get integration package for a case
 */
export const getIntegrationPackage = async (packageId: string): Promise<IntegrationPackage> => {
  const response = await designClient.get(`/code-generation/packages/${packageId}`);
  return response.data;
};

/**
 * Get package summary for a case
 */
export const getPackageSummary = async (packageId: string): Promise<PackageSummary> => {
  const response = await designClient.get(`/code-generation/packages/${packageId}/summary`);
  return response.data;
};

/**
 * Get all packages for a case
 */
export const getPackagesForCase = async (caseId: string): Promise<PackageSummary[]> => {
  const response = await designClient.get(`/code-generation/cases/${caseId}/packages`);
  return response.data;
};

/**
 * Get a specific artifact from a package
 */
export const getArtifact = async (
  packageId: string,
  artifactId: string
): Promise<CodeArtifact> => {
  const response = await designClient.get(
    `/code-generation/packages/${packageId}/artifacts/${artifactId}`
  );
  return response.data;
};

/**
 * Download a specific artifact
 */
export const downloadArtifact = async (
  packageId: string,
  artifactId: string
): Promise<{ filename: string; content: string; content_type: string }> => {
  const response = await designClient.get(
    `/code-generation/packages/${packageId}/artifacts/${artifactId}/download`
  );
  return response.data;
};

/**
 * Download entire package as ZIP
 */
export const downloadPackage = async (packageId: string): Promise<Blob> => {
  const response = await designClient.get(
    `/code-generation/packages/${packageId}/download`,
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Get artifacts by category
 */
export const getArtifactsByCategory = async (
  packageId: string,
  category: string
): Promise<CodeArtifact[]> => {
  const response = await designClient.get(
    `/code-generation/packages/${packageId}/artifacts`,
    { params: { category } }
  );
  return response.data;
};

/**
 * Regenerate a specific artifact
 */
export const regenerateArtifact = async (
  packageId: string,
  artifactId: string
): Promise<CodeArtifact> => {
  const response = await designClient.post(
    `/code-generation/packages/${packageId}/artifacts/${artifactId}/regenerate`
  );
  return response.data;
};

/**
 * Validate generated code
 */
export const validatePackage = async (
  packageId: string
): Promise<{
  valid: boolean;
  total_errors: number;
  total_warnings: number;
  artifact_results: Array<{
    artifact_id: string;
    valid: boolean;
    errors: string[];
    warnings: string[];
  }>;
}> => {
  const response = await designClient.post(
    `/code-generation/packages/${packageId}/validate`
  );
  return response.data;
};

// =============================================================================
// Export all functions
// =============================================================================

export default {
  // Data Flow Mapping
  generateDataFlow,
  getDataFlow,
  getColumnMappings,
  updateColumnMapping,
  createColumnMapping,
  saveDataFlow,
  runAiInference,
  getRelationshipMappings,
  getUnmappedColumns,
  getMappingSuggestions,
  getCoverageSummary,
  getDataFlowStats,

  // Integration Pattern
  generateIntegrationPlan,
  getIntegrationPlanSummary,
  getIntegrationPlan,
  getEntityPlans,
  getEntityPlan,
  updateEntityPattern,
  getPatternRecommendations,
  getPatternComparison,
  getInfrastructureRequirements,
  getExecutionPhases,
  confirmIntegrationPlan,
  getQuestionCoverage,

  // Code Generation
  generateIntegrationPackage,
  getIntegrationPackage,
  getPackageSummary,
  getPackagesForCase,
  getArtifact,
  downloadArtifact,
  downloadPackage,
  getArtifactsByCategory,
  regenerateArtifact,
  validatePackage,
};
